import { Progress } from '@heroui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { settingsApi, storageApi, type TenantSettings } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { formatBytes, formatNumber } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  DataTable,
  ErrorState,
  LoadingState,
  NumberField,
  PageHeader,
  Panel,
  ReadOnlyField,
  SaveErrorNotice,
  useSaveErrors,
  useSaveFailureFocus,
  useServerForm,
  useToast,
  type DataColumn,
} from '../../components/ui/index.js';

const policyFrom = (settings: TenantSettings) => ({
  retentionDays: settings.fileRetentionDays as number | null,
  quotaBytes: settings.storageQuotaBytes as number | null,
});

/**
 * Storage administration.
 *
 * Reachable only with `storage.manage`, which in the default role bundles
 * belongs to the Manager alone. That gating is a COURTESY: every endpoint
 * behind this page re-checks the same permission server-side, so removing the
 * route would change what is visible and nothing about what is possible.
 *
 * Three honest things this page does, which a usage dial alone would not:
 *
 *  - **Retention is opt-in.** An empty retention field means "keep until
 *    someone removes it", and says so. A blank is never read as zero
 *    (CLAUDE.md section 8).
 *  - **The purge can be rehearsed.** The default action reports what WOULD
 *    happen and changes nothing; applying it is a separate, deliberate press.
 *  - **Integrity is checked in both directions** — a record whose file is
 *    gone, and a file with no record — and reports what it actually found,
 *    including "nothing", rather than implying health by silence.
 */
export function StoragePage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const locale = currentLocale();
  const queryClient = useQueryClient();
  const { show } = useToast();

  const canEditSettings = can(PERMISSIONS.SETTINGS_UPDATE);

  const usage = useQuery({ queryKey: ['storage', 'usage'], queryFn: storageApi.usage });
  const settings = useQuery({ queryKey: ['settings'], queryFn: settingsApi.get });

  // Not pre-filled with a default: the laboratory's own values arrive from the
  // backend, which is the single place a default for a new organisation lives.
  // Authoritative while edited; reset only by this page's own success (D205).
  const policy = useServerForm(settings.data, policyFrom, {
    retentionDays: null,
    quotaBytes: null,
  });
  const { retentionDays, quotaBytes } = policy.values;

  const savePolicy = useMutation({
    mutationFn: ({ values }: { values: typeof policy.values; mark: number }) =>
      settingsApi.update({
        // An empty field clears the rule; it is never coerced into a number.
        fileRetentionDays: values.retentionDays,
        storageQuotaBytes: values.quotaBytes,
      }),
    onSuccess: async (saved, { mark }) => {
      policy.saved(saved, mark);
      queryClient.setQueryData(['settings'], saved);
      show({ tone: 'success', message: t('settings.savedNotice') });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      await queryClient.invalidateQueries({ queryKey: ['storage'] });
    },
  });

  const { fields: policyErrors } = useSaveErrors(savePolicy.error);
  const policyRef = useRef<HTMLDivElement>(null);
  const policyNoticeRef = useRef<HTMLDivElement>(null);
  useSaveFailureFocus(savePolicy.error, policyRef, policyNoticeRef);

  const orphans = useMutation({ mutationFn: storageApi.orphans });

  const retention = useMutation({
    mutationFn: (dryRun: boolean) => storageApi.runRetention({ dryRun }),
    onSuccess: async (result) => {
      show({
        tone: result.dryRun ? 'info' : 'success',
        message: t(
          result.dryRun
            ? 'storage.retentionDryRunResult'
            : 'storage.retentionResult',
          {
            expired: formatNumber(result.expired, { locale }) ?? '',
            purged: formatNumber(result.purged, { locale }) ?? '',
            bytes: formatBytes(result.bytesReclaimed, { locale }) ?? '',
          },
        ),
      });
      if (!result.dryRun) {
        await queryClient.invalidateQueries({ queryKey: ['storage'] });
      }
    },
  });

  if (usage.isLoading || settings.isLoading) return <LoadingState />;
  if (usage.error) {
    return <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />;
  }

  const data = usage.data;
  // Not an error state with nothing in it: react-query can be neither loading
  // nor failed while the first result is still settling.
  if (!data) return <LoadingState />;

  const size = (bytes: number) => formatBytes(bytes, { locale }) ?? '';
  const count = (value: number) => formatNumber(value, { locale }) ?? '';

  const percentUsed =
    data.quotaBytes > 0
      ? Math.min(100, Math.round((data.usedBytes / data.quotaBytes) * 100))
      : 0;

  const breakdownColumns: DataColumn<(typeof data.byEntityType)[number]>[] = [
    {
      key: 'entityType',
      header: t('storage.entityType'),
      render: (row) => row.entityType,
    },
    {
      key: 'fileCount',
      header: t('storage.fileCount'),
      render: (row) => count(row.fileCount),
    },
    {
      key: 'bytes',
      header: t('storage.bytes'),
      render: (row) => size(row.bytes),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('storage.title')}
        subtitle={t('storage.subtitle')}
        breadcrumbs={[{ label: t('nav.storage') }]}
      />

      <div className="grid gap-gutter lg:grid-cols-2">
        <Panel title={t('storage.usage')}>
          <div className="flex flex-col gap-gutter">
            <div>
              <Progress
                value={percentUsed}
                size="md"
                aria-label={t('storage.usage')}
                color={percentUsed >= 90 ? 'danger' : 'primary'}
              />
              <p className="mt-1 text-caption text-default-600">
                {t('storage.usedOfQuota', {
                  used: size(data.usedBytes),
                  quota: size(data.quotaBytes),
                })}
              </p>
            </div>

            <div className="grid gap-stack sm:grid-cols-2">
              <ReadOnlyField label={t('storage.fileCount')}>
                  {count(data.fileCount)}
                </ReadOnlyField>
              <ReadOnlyField label={t('storage.maxUpload')}>
                  {size(data.maxUploadBytes)}
                </ReadOnlyField>
              <ReadOnlyField label={t('storage.deletedFiles')}>
                  {`${count(data.deletedFileCount)} · ${size(data.deletedBytes)}`}
                </ReadOnlyField>
              <ReadOnlyField label={t('storage.retentionDays')}>
                  {data.retentionDays === null
                    ? t('common.notSet')
                    : count(data.retentionDays)}
                </ReadOnlyField>
            </div>
          </div>
        </Panel>

        <Panel title={t('storage.byEntityType')}>
          <DataTable
            label={t('storage.byEntityType')}
            columns={breakdownColumns}
            rows={data.byEntityType}
            rowKey={(row) => row.entityType}
          />
        </Panel>

        <Panel title={t('storage.integrity')}>
          <div className="flex flex-col gap-gutter">
            <p className="text-caption text-default-600">
              {t('storage.integrityHint')}
            </p>

            <ActionBar>
              <ActionButton
                priority="secondary"
                isLoading={orphans.isPending}
                onPress={() => orphans.mutate()}
              >
                {t('storage.checkIntegrity')}
              </ActionButton>
            </ActionBar>

            {orphans.error ? <ErrorState error={orphans.error} /> : null}

            {orphans.data ? (
              <div className="grid gap-stack sm:grid-cols-3">
                <ReadOnlyField label={t('storage.missingObjects')}>
                    {count(orphans.data.missingObjects.length)}
                  </ReadOnlyField>
                <ReadOnlyField label={t('storage.orphanedObjects')}>
                    {count(orphans.data.orphanedObjectCount)}
                  </ReadOnlyField>
                <ReadOnlyField label={t('storage.scannedObjects')}>
                    {count(orphans.data.scannedObjectCount)}
                  </ReadOnlyField>
              </div>
            ) : null}

            {/*
              "Nothing found" is stated, not implied by an absence of output.
              A blank panel reads as "not run yet", which is a different fact.
            */}
            {orphans.data &&
            orphans.data.missingObjects.length === 0 &&
            orphans.data.orphanedObjectCount === 0 ? (
              <Alert tone="success" title={t('storage.integrityClean')} />
            ) : null}
          </div>
        </Panel>

        <Panel title={t('storage.retention')}>
          <div ref={policyRef} className="flex flex-col gap-gutter">
            <NumberField
              label={t('storage.retentionDays')}
              description={t('storage.retentionHint')}
              value={retentionDays}
              onChange={(value) => policy.set('retentionDays', value)}
              {...(policyErrors.fileRetentionDays ? { error: policyErrors.fileRetentionDays } : {})}
              isDisabled={!canEditSettings}
            />
            <NumberField
              label={t('storage.quotaBytes')}
              description={t('storage.quotaHint')}
              value={quotaBytes}
              onChange={(value) => policy.set('quotaBytes', value)}
              {...(policyErrors.storageQuotaBytes ? { error: policyErrors.storageQuotaBytes } : {})}
              isDisabled={!canEditSettings}
            />

            {savePolicy.error ? (
              <div ref={policyNoticeRef} tabIndex={-1} className="outline-none">
                <SaveErrorNotice error={savePolicy.error} title={t('settings.saveFailed')} />
              </div>
            ) : null}
            {retention.error ? <ErrorState error={retention.error} /> : null}

            <ActionBar>
              {canEditSettings ? (
                <ActionButton
                  priority="primary"
                  isLoading={savePolicy.isPending}
                  onPress={() => savePolicy.mutate({ values: policy.values, mark: policy.mark() })}
                >
                  {t('settings.save')}
                </ActionButton>
              ) : null}

              {/*
                The rehearsal is the SECONDARY action and the apply is the
                destructive one, so the safe path is the one nearest to hand.
              */}
              <ActionButton
                priority="secondary"
                isLoading={retention.isPending}
                onPress={() => retention.mutate(true)}
              >
                {t('storage.rehearseRetention')}
              </ActionButton>
              <ActionButton
                priority="destructive"
                isLoading={retention.isPending}
                onPress={() => retention.mutate(false)}
              >
                {t('storage.runRetention')}
              </ActionButton>
            </ActionBar>
          </div>
        </Panel>
      </div>
    </>
  );
}
